import { useState, useRef, useEffect } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d706nhedp0k8rarn88sg';

const timeSlots = [
  '9:00 AM EST', '10:00 AM EST', '11:00 AM EST',
  '1:00 PM EST', '2:00 PM EST', '3:00 PM EST', '4:00 PM EST',
];

export default function ConsultationSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('[data-animate]').forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = '1';
                (el as HTMLElement).style.transform = 'translateY(0)';
              }, i * 120);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const params = new URLSearchParams();
    data.forEach((value, key) => params.append(key, String(value)));

    setStatus('loading');
    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  const inputClass =
    'w-full px-4 py-3.5 bg-stone-50 border border-stone-200 rounded-sm text-sm text-stone-800 placeholder-stone-400 focus:outline-none focus:border-amber-400 transition-colors duration-200';
  const inputStyle = { fontFamily: "'DM Sans', sans-serif", fontWeight: 300 };

  return (
    <section id="consultation" ref={sectionRef} className="py-28 px-6 lg:px-12 overflow-hidden" style={{ background: '#FAF7F3' }}>
      <div className="max-w-3xl mx-auto">

        {/* Centered Header */}
        <div
          data-animate
          className="text-center mb-14"
          style={{ opacity: 0, transform: 'translateY(28px)', transition: 'all 0.7s ease' }}
        >
          <p
            className="inline-block text-xs tracking-widest uppercase px-4 py-1.5 border border-amber-300/60 text-amber-700 mb-6 rounded-sm"
            style={{ fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.18em', fontSize: '0.65rem' }}
          >
            Free Consultation
          </p>
          <h2
            className="text-4xl lg:text-5xl text-stone-800 mb-5"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, lineHeight: 1.2 }}
          >
            Ready to Bring Your<br />
            <em style={{ fontStyle: 'italic', color: '#B08A4C' }}>Dream Dress</em> to Life?
          </h2>
          <p
            className="text-stone-500 leading-relaxed mx-auto"
            style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300, fontSize: '0.95rem', maxWidth: '26rem' }}
          >
            Start with a free consultation. Upload your inspiration, and our design team will reach out within 24 hours to begin your bridal journey.
          </p>
        </div>

        {/* Form Card */}
        <div
          data-animate
          className="bg-white rounded-lg p-10"
          style={{ opacity: 0, transform: 'translateY(28px)', transition: 'all 0.7s ease 0.15s' }}
        >
          {status === 'success' ? (
            <div className="flex flex-col items-center justify-center py-16 text-center gap-5">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center"
                style={{ background: '#F0E6D4' }}
              >
                <i className="ri-heart-line" style={{ color: '#B08A4C', fontSize: '1.6rem' }} />
              </div>
              <h3
                className="text-2xl text-stone-800"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}
              >
                We can&apos;t wait to meet you!
              </h3>
              <p
                className="text-stone-500 text-sm max-w-xs leading-relaxed"
                style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
              >
                Your consultation request has been received. Our team will reach out within 24 hours to confirm your appointment.
              </p>
              <button
                onClick={() => setStatus('idle')}
                className="mt-4 text-xs tracking-widest uppercase text-amber-600 hover:text-amber-700 cursor-pointer transition-colors"
                style={{ fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.12em' }}
              >
                Submit another request
              </button>
            </div>
          ) : (
            <>
              <p
                className="text-stone-400 text-sm mb-8 text-center"
                style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
              >
                Complimentary &middot; No commitment
              </p>

              <form
                onSubmit={handleSubmit}
                data-readdy-form
                className="flex flex-col gap-4"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-stone-500 mb-1.5 tracking-wide" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="Your full name"
                      className={inputClass}
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-stone-500 mb-1.5 tracking-wide" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="your@email.com"
                      className={inputClass}
                      style={inputStyle}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-stone-500 mb-1.5 tracking-wide" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="+1 (555) 000-0000"
                      className={inputClass}
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-stone-500 mb-1.5 tracking-wide" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                      Wedding Date *
                    </label>
                    <input
                      type="date"
                      name="wedding_date"
                      required
                      className={inputClass}
                      style={inputStyle}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs text-stone-500 mb-1.5 tracking-wide" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                    Preferred Consultation Time *
                  </label>
                  <select
                    name="preferred_time"
                    required
                    className={inputClass}
                    style={inputStyle}
                  >
                    <option value="">Select a time slot</option>
                    {timeSlots.map((slot) => (
                      <option key={slot} value={slot}>{slot}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs text-stone-500 mb-1.5 tracking-wide" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                    Budget Range
                  </label>
                  <select
                    name="budget_range"
                    className={inputClass}
                    style={inputStyle}
                  >
                    <option value="">Select your budget range</option>
                    <option value="Essential ($4,200)">Essential — $4,200</option>
                    <option value="Signature ($5,400)">Signature — $5,400</option>
                    <option value="Couture ($6,700)">Couture — $6,700</option>
                    <option value="Not sure yet">Not sure yet — let&apos;s talk</option>
                  </select>
                  <p className="text-xs text-stone-400 mt-1.5" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>
                    Helps us prepare the right options for your consultation
                  </p>
                </div>

                <div>
                  <label className="block text-xs text-stone-500 mb-1.5 tracking-wide" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                    Tell Us About Your Dream Dress
                  </label>
                  <textarea
                    name="dress_vision"
                    rows={4}
                    maxLength={500}
                    placeholder="Describe your ideal gown — silhouette, fabric, vibe, details you love..."
                    className={`${inputClass} resize-none`}
                    style={inputStyle}
                    onChange={(e) => setCharCount(e.target.value.length)}
                  />
                  <div className="flex justify-between mt-1">
                    <span className="text-xs text-stone-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>Optional but helpful</span>
                    <span
                      className={`text-xs ${charCount >= 480 ? 'text-amber-600' : 'text-stone-400'}`}
                      style={{ fontFamily: "'DM Sans', sans-serif" }}
                    >
                      {charCount}/500
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-xs text-stone-500 mb-1.5 tracking-wide" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                    Inspiration Image or Link
                  </label>
                  <input
                    type="url"
                    name="inspiration_link"
                    placeholder="Paste a link — Pinterest, Instagram, or any image URL"
                    className={inputClass}
                    style={inputStyle}
                  />
                  <p className="text-xs text-stone-400 mt-1.5" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>
                    You can also email photos directly to hello@dreamlinebridal.com
                  </p>
                </div>

                {status === 'error' && (
                  <p className="text-sm text-red-500" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                    Something went wrong. Please try again or email us directly.
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="w-full py-4 mt-2 flex items-center justify-center gap-3 text-xs tracking-widest uppercase transition-all duration-300 cursor-pointer rounded-sm whitespace-nowrap disabled:opacity-60"
                  style={{
                    background: '#1C120A',
                    color: '#FAF7F3',
                    fontFamily: "'DM Sans', sans-serif",
                    letterSpacing: '0.14em',
                  }}
                >
                  {status === 'loading' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <span>Schedule My Consultation</span>
                      <i className="ri-arrow-right-line" />
                    </>
                  )}
                </button>
              </form>
            </>
          )}
        </div>

      </div>
    </section>
  );
}
